import React, { useState, useContext, createContext, lazy, Suspense, useEffect } from 'react';
import { 
  Box, Fab, Zoom, CircularProgress, Button, TextField, Badge,
  DialogTitle, DialogContent, DialogActions, FormControl, 
  InputLabel, Select, MenuItem, Typography, Paper, IconButton, Tooltip, Tabs, Tab
} from '@mui/material';

import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';

import AuthContext from "./auth/Context";
import SidebarMenu from './SidebarMenu';
import Drawer from "./ManagedModal";
import { useModalManager } from './ModalManagerContext';

const AiChatManager = lazy(() => import('./AiChatManager'));
// Báo cáo dùng token nạp LƯỜI: chỉ tải khi quản trị thật sự mở tab đó.
const AiUsageReport = lazy(() => import('./AiUsageReport'));

export const AppLayoutContext = createContext();

// localStorage key để nhớ user đã thấy intro (xoá dòng này nếu muốn hiện mỗi lần refresh)
const INTRO_SEEN_KEY = 'ai_chat_intro_seen';

export default function AppLayout({ children, menuData = [], showSidebarMenu = true }) {
  const context = useContext(AuthContext);
  const modalManager = useModalManager();
  const zIndex = modalManager.requestZIndex();

  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');
  
  const [mobileOpen, setMobileOpen] = useState(false);

  // ── CHAT STATE ──────────────────────────────────────────────────────────────
  const [chatOpen, setChatOpen]               = useState(false);
  const [chatInitialized, setChatInitialized] = useState(false);

  // ── INTRO TOOLTIP STATE ─────────────────────────────────────────────────────
  const [showIntro, setShowIntro] = useState(false);

  // ── SETUP AI STATE ──────────────────────────────────────────────────────────
  const [setupOpen, setSetupOpen]               = useState(false);
  const [setupTab, setSetupTab]                 = useState('cau_hinh'); // 'cau_hinh' | 'su_dung'
  const [isSubmittingSetup, setIsSubmittingSetup] = useState(false);
  // ai_model_light: model "nhẹ" (rẻ hơn) cho các tác vụ đơn giản — định tuyến nhanh, phân loại,
  // trích xuất. Cùng nền tảng (ai_name) và API Key với model chính, chỉ khác tên model.
  // Bỏ trống -> máy chủ tự dùng ai_model. Vì vậy trường này KHÔNG bắt buộc.
  const [aiSetupData, setAiSetupData] = useState({ ai_name: 'GPT', ai_api_key: '', ai_model: '', ai_model_light: '' });

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const finalMenuData = (menuData.length > 0 ? menuData : (context.modules || [])).map(m => {
    for (let item of (m.items || [])) {
      if (item?.path?.startsWith("/report/")) item.path = `/page${item.path}`;
    }
    return m;
  });

  const hasAiKey   = !!context.appInfo?.ai_api_key && !!context.appInfo?.ai_model;
  const isAppAdmin = !!context.appInfo?.appAdmin;

  // MÁY CHỦ CÓ GÓI AI AGENT KHÔNG?
  // Gói agent (@pvtruong/server-agent) là tuỳ chọn — bản triển khai chỉ-core hoặc cấu hình tắt gói
  // thì mọi endpoint chat/agent đều không tồn tại. Trước đây nút vẫn hiện, bấm vào là 404 và người
  // dùng (nhất là quản trị công ty) tưởng hệ thống hỏng. Nay hỏi máy chủ qua /api/features.
  // Máy chủ đời cũ chưa có endpoint đó -> hasFeature trả true -> giữ nguyên hành vi cũ, không ẩn nhầm.
  const aiAvailable = context.hasFeature ? context.hasFeature('ai_agent') : true;
  const showAiButton = aiAvailable && (hasAiKey || isAppAdmin);

  // ── HIỆN INTRO SAU 1.5S KHI NÚT AI SẴN SÀNG ───────────────────────────────
  useEffect(() => {
    if (!showAiButton) return;
    if (localStorage.getItem(INTRO_SEEN_KEY)) return;

    const showTimer = setTimeout(() => setShowIntro(true), 1500);
    return () => clearTimeout(showTimer);
  }, [showAiButton]);

  // Tự ẩn intro sau 12s nếu user không tương tác
  useEffect(() => {
    if (!showIntro) return;
    const autoHide = setTimeout(() => setShowIntro(false), 12000);
    return () => clearTimeout(autoHide);
  }, [showIntro]);

  const dismissIntro = () => {
    setShowIntro(false);
    localStorage.setItem(INTRO_SEEN_KEY, '1');
  };

  // ── MỞ CHAT ────────────────────────────────────────────────────────────────
  const openChat = () => {
    setChatInitialized(true);
    setChatOpen(true);
    dismissIntro(); // luôn ẩn intro khi mở chat
  };

  // ── MỞ DIALOG CẤU HÌNH AI ──────────────────────────────────────────────────
  // Tách riêng vì có HAI đường vào: nút FAB khi công ty chưa khai API Key, và nút bánh răng
  // dành cho quản trị khi đã khai rồi (trước đây khai xong là không còn đường quay lại
  // dialog này, nên không sửa/bổ sung được model nhẹ).
  const openAiSetup = () => {
    if (!isAppAdmin) return;
    setAiSetupData({
      ai_name:        context.appInfo?.ai_name        || 'GPT',
      ai_api_key:     '',   // luôn để trống: trống = giữ key cũ (xem handleSaveAiSetup)
      ai_model:       context.appInfo?.ai_model       || '',
      ai_model_light: context.appInfo?.ai_model_light || ''
    });
    dismissIntro();
    setSetupTab('cau_hinh');
    setSetupOpen(true);
  };

  const handleAiButtonClick = () => {
    if (chatInitialized || hasAiKey) {
      openChat();
    } else if (isAppAdmin) {
      openAiSetup();
    }
  };

  // ── LƯU CẤU HÌNH AI ────────────────────────────────────────────────────────
  const handleSaveAiSetup = async () => {
    const daCoCauHinh = hasAiKey; // trạng thái TRƯỚC khi lưu: lần khai đầu hay chỉ vào sửa
    const apiKeyMoi   = aiSetupData.ai_api_key.trim();

    // API Key chỉ bắt buộc ở LẦN KHAI ĐẦU. Khi đã khai rồi mà ô này trống thì hiểu là
    // "giữ nguyên key cũ" — không gửi lên, để không có đường nào xoá trắng key đang chạy.
    if ((!daCoCauHinh && !apiKeyMoi) || !aiSetupData.ai_model.trim()) {
      context.alert?.("Vui lòng nhập đầy đủ API Key và Model!");
      return;
    }
    setIsSubmittingSetup(true);
    try {
      const payload = {
        _id:            context.appInfo._id,
        ai_name:        aiSetupData.ai_name,
        ai_model:       aiSetupData.ai_model.trim(),
        // Gửi cả khi rỗng: người dùng xoá trường này = muốn quay về dùng model chính.
        ai_model_light: aiSetupData.ai_model_light.trim()
      };
      if (apiKeyMoi) payload.ai_api_key = apiKeyMoi;
      const res = await context.apis?.asyncPostList(userToken, "app", payload);
      if (res && !res.error) {
        context.setApp(context.userInfo, { ...context.appInfo, ...payload });
        setSetupOpen(false);
        // Lần khai đầu tiên thì mở chat luôn; còn nếu chỉ vào sửa cấu hình thì đừng
        // bật khung chat lên giữa lúc người dùng đang làm việc khác.
        if (!daCoCauHinh) openChat();
      } else {
        context.alert?.(res?.error || "Lỗi cập nhật cấu hình hệ thống!");
      }
    } catch (error) {
      console.error("Lỗi setup AI:", error);
      context.alert?.("Lỗi kết nối, vui lòng thử lại!");
    } finally {
      setIsSubmittingSetup(false);
    }
  };

  return (
    <AppLayoutContext.Provider value={{ handleDrawerToggle, openAiSetup }}>
      <Box sx={{
        display: 'flex', height: '100dvh', width: '100%',
        bgcolor: 'background.default', overflow: 'hidden', position: 'relative',
      }}>
        
        {/* SIDEBAR */}
        {showSidebarMenu && finalMenuData.length > 0 && (
          <SidebarMenu menuData={finalMenuData} mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />
        )}

        {/* MAIN CONTENT */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, width: '100%', overflow: 'hidden' }}>
          {children}
        </Box>

        {/* ── INTRO SPEECH BUBBLE ──────────────────────────────────────────────
            Hiện bên cạnh FAB để giới thiệu AI với user lần đầu.
            Mũi tên nhỏ bên phải bubble chỉ vào nút FAB.
        */}
        <Zoom in={showIntro && !chatOpen }>
          <Box sx={{
            position:      'absolute',
            bottom:        50,
            right:         68, // đủ khoảng cách cho FAB (40px) + mũi tên (10px) + gap (2px)
            zIndex:        theme => theme.zIndex.speedDial + 99997,
            transformOrigin: 'bottom right',
            // Hiệu ứng bounce khi xuất hiện (chỉ chạy 1 lần khi mount)
            animation:     'aiBubbleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both',
            '@keyframes aiBubbleIn': {
              from: { opacity: 0, transform: 'scale(0.6) translateX(12px)' },
              to:   { opacity: 1, transform: 'scale(1)  translateX(0)' },
            },
          }}>
            <Paper
              elevation={8}
              sx={{
                p: 2, borderRadius: 3,
                bgcolor: '#007aff', color: '#fff',
                maxWidth: 248, position: 'relative',
                // Mũi tên CSS tam giác trỏ sang phải → FAB
                '&::after': {
                  content:       '""',
                  position:      'absolute',
                  right:         -9,
                  bottom:        16,
                  borderWidth:   '7px 0 7px 9px',
                  borderStyle:   'solid',
                  borderColor:   'transparent transparent transparent #007aff',
                },
              }}
            >
              {/* Nút đóng */}
              <IconButton
                size="small"
                onClick={dismissIntro}
                sx={{
                  position: 'absolute', top: 6, right: 6,
                  color: 'rgba(255,255,255,0.65)',
                  '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>

              {/* Nội dung */}
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.75, pr: 3, fontSize: '0.875rem' }}>
                👋 Xin chào Sếp!
              </Typography>

              {/* Năng lực nổi bật — 3 dòng ngắn, đủ gây ấn tượng */}
              <Box component="ul" sx={{ m: 0, pl: '1.1em', fontSize: '0.78rem', lineHeight: 1.7, opacity: 0.95 }}>
                <Box component="li">📊 Báo cáo tài chính, kho, công nợ, nhân sự tức thì</Box>
                <Box component="li">⚡ Lập KPI, quản trị mục tiêu, giao việc, tạo chứng từ, gửi email, lập lịch tự động</Box>
                <Box component="li">🔍 Tra cứu dữ liệu & dẫn đến đúng chức năng cần dùng</Box>
              </Box>

              {/* CTA */}
              <Button
                fullWidth size="small"
                onClick={() => { dismissIntro(); openChat(); }}
                sx={{
                  mt: 1.5, fontWeight: 'bold', textTransform: 'none',
                  bgcolor: '#fff', color: '#007aff', borderRadius: 2,
                  fontSize: '0.8rem',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.88)' },
                }}
              >
                Hỏi tôi bất cứ điều gì →
              </Button>
            </Paper>
          </Box>
        </Zoom>

        {/* ── FAB NÚT MỞ CHAT ──────────────────────────────────────────────────
            • Badge xanh: báo có session đang minimize
            • Hiệu ứng pulse: chạy khi intro đang hiển thị để thu hút ánh nhìn
        */}
        <Zoom in={showAiButton && !chatOpen && !setupOpen}>
          <Box sx={{
            position: 'absolute', bottom: 56, right: 16,
            zIndex: theme => theme.zIndex.speedDial + 99999,
          }}>
            {/* Bánh răng: đường quay lại dialog cấu hình sau khi đã khai API Key —
                cần thiết để quản trị bổ sung/sửa "model nhẹ" về sau. */}
            {isAppAdmin && (
              <Tooltip title="Cấu hình Trợ lý AI" placement="left">
                <IconButton
                  aria-label="ai-settings"
                  size="small"
                  onClick={openAiSetup}
                  sx={{
                    position: 'absolute', top: -34, right: 2,
                    width: 28, height: 28,
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}

            <Badge color="success" variant="dot" invisible={!chatInitialized}>
              <Fab
                color="primary"
                aria-label="chat-with-ai"
                onClick={handleAiButtonClick}
                size="small"
                sx={{
                  boxShadow: '0 4px 20px rgba(0,122,255,0.35)',
                  // Pulse khi intro đang hiện → kéo sự chú ý về nút
                  animation: showIntro ? 'aiPulse 1.6s ease-out infinite' : 'none',
                  '@keyframes aiPulse': {
                    '0%':   { boxShadow: '0 0 0 0   rgba(0,122,255,0.65)' },
                    '65%':  { boxShadow: '0 0 0 12px rgba(0,122,255,0)' },
                    '100%': { boxShadow: '0 0 0 0   rgba(0,122,255,0)' },
                  },
                }}
              >
                <SmartToyIcon />
              </Fab>
            </Badge>
          </Box>
        </Zoom>

        {/* ── CHAT OVERLAY (keep-mounted via CSS transform) ─────────────────── */}
        {/* Khung chat chỉ tồn tại khi máy chủ THẬT SỰ có gói AI agent — chặn cả trường hợp nơi khác
            gọi nhầm openChat() trên bản triển khai không có agent. */}
        {aiAvailable && chatInitialized && (
          <>
            {/* Backdrop */}
            <Box
              onClick={() => setChatOpen(false)}
              sx={{
                position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.5)', zIndex: 1199,
                opacity:       chatOpen ? 1 : 0,
                pointerEvents: chatOpen ? 'auto' : 'none',
                transition:    'opacity 0.3s ease',
              }}
            />
            {/* Chat Slide Panel */}
            <Box sx={{
              position: 'fixed', top: 0, right: 0, bottom: 0,
              width: { xs: '100%', sm: '100%', md: '90%' },
              zIndex:zIndex,
              bgcolor: 'background.paper',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
              transform:  chatOpen ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
              <Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                  <CircularProgress color="primary" />
                </Box>
              }>
                <AiChatManager
                  onClose={()    => setChatOpen(false)}
                  onMinimize={() => setChatOpen(false)}
                />
              </Suspense>
            </Box>
          </>
        )}

        {/* ── DIALOG CẤU HÌNH AI (chỉ Admin) ──────────────────────────────── */}
        {isAppAdmin && (
          <Drawer
            type="dialog" open={setupOpen}
            onClose={() => !isSubmittingSetup && setSetupOpen(false)}
            maxWidth={setupTab === 'su_dung' ? 'md' : 'sm'} fullWidth
          >
            <DialogTitle sx={{ fontWeight: 'bold', pb: 0 }}>Trợ lý AI</DialogTitle>
            <Tabs
              value={setupTab}
              onChange={(e, v) => setSetupTab(v)}
              sx={{ px: 3, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
            >
              <Tab value="cau_hinh" label="Cấu hình" sx={{ textTransform: 'none', minHeight: 40 }} />
              <Tab value="su_dung"  label="Sử dụng token" sx={{ textTransform: 'none', minHeight: 40 }} />
            </Tabs>
            <DialogContent>
              {setupTab === 'su_dung' ? (
                <Suspense fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                  </Box>
                }>
                  <AiUsageReport context={context} />
                </Suspense>
              ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Nền tảng AI</InputLabel>
                  <Select value={aiSetupData.ai_name} label="Nền tảng AI"
                    onChange={(e) => setAiSetupData({ ...aiSetupData, ai_name: e.target.value })}
                    disabled={isSubmittingSetup}>
                    <MenuItem value="GPT">OpenAI (GPT)</MenuItem>
                    <MenuItem value="CLAUDE">Anthropic (Claude)</MenuItem>
                    <MenuItem value="GEMINI">Google (Gemini)</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="API Key" fullWidth size="small" type="password"
                  value={aiSetupData.ai_api_key}
                  onChange={(e) => setAiSetupData({ ...aiSetupData, ai_api_key: e.target.value })}
                  disabled={isSubmittingSetup}
                  placeholder={hasAiKey ? "Đã lưu — để trống nếu không đổi" : "Nhập API Key của bạn..."}
                  helperText={hasAiKey ? "Để trống thì giữ nguyên API Key đang dùng." : ""} />
                <TextField label="AI Model" fullWidth size="small"
                  value={aiSetupData.ai_model}
                  onChange={(e) => setAiSetupData({ ...aiSetupData, ai_model: e.target.value })}
                  disabled={isSubmittingSetup} placeholder="VD: gpt-4o, claude-sonnet-4-6, gemini-1.5-pro..."
                  helperText="Model chính — dùng cho các tác vụ phức tạp, nhiều bước." />

                <TextField label="Model nhẹ (tuỳ chọn)" fullWidth size="small"
                  value={aiSetupData.ai_model_light}
                  onChange={(e) => setAiSetupData({ ...aiSetupData, ai_model_light: e.target.value })}
                  disabled={isSubmittingSetup} placeholder="VD: gpt-4o-mini, claude-3-5-haiku, gemini-1.5-flash..."
                  helperText="Model rẻ hơn cho tác vụ nhẹ: định tuyến câu hỏi, phân loại, trích xuất. Cùng nền tảng và API Key với model chính. Bỏ trống thì dùng luôn model chính." />
              </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setSetupOpen(false)} color="inherit" disabled={isSubmittingSetup}>
                {setupTab === 'su_dung' ? 'Đóng' : 'Huỷ'}
              </Button>
              {setupTab === 'cau_hinh' && (
                <Button onClick={handleSaveAiSetup} variant="contained" disabled={isSubmittingSetup}>
                  {isSubmittingSetup ? <CircularProgress size={20} color="inherit" /> : 'Lưu cấu hình'}
                </Button>
              )}
            </DialogActions>
          </Drawer>
        )}

      </Box>
    </AppLayoutContext.Provider>
  );
}